/**
 * A Suggestion Engine strategy card for the village module, in the aged care card's anatomy.
 *
 * WHY (owner, 24 Sep 2026: "the suggestion engine results cards look and are positioned
 * differently … try to get harmonisation of the UI/UX"). Aged Care's card reads top to bottom:
 * what the plan is, WHAT IT DOES (the money flow and the plan in one sentence), the figures with
 * the baseline under each, then Explain / Deep dive / Select. The village card carried the same
 * facts in a different order and a different dress. Same order, same dress now.
 *
 * THE RAIL HOLDS STAYING PUT in engine mode, so this card is the only place a plan is described:
 * the in-brief sentence here is the SAME template Manual Control's rail uses (rvInBrief.tsx),
 * measured against the same staying-put baseline, and every figure under a tile says what staying
 * put gives and how far this plan moves it. Nothing here is re-derived: the strategy's own
 * snapshot and projections, and the baseline candidate's, quoted.
 *
 * WORTH KNOWING, ON THE CARD (owner, 24 Sep 2026: "you're going to get me sued if a client pleads
 * ignorance"). A lifetime annuity that returns nothing on death is said in amber on the card
 * itself, with the age-and-health warning the aged care cards carry — never only inside "Explain
 * this".
 */
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import React from 'react';
import { BookOpen, CheckCircle, ShieldAlert } from 'lucide-react';
import type { AlternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { Badge, Button } from '../../components/ui/index.tsx';
import { PlainWordsCard, MoneyFlow, type MoneyFlowStep } from '../../components/narrative/PlainWords.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { PENSION_AGE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import RvStrategyExplainer from './RvStrategyExplainer.tsx';
import { RvInBrief, nilResidualAnnuityText, type RvInBriefAnnuity } from './rvInBrief.tsx';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';
import type { RvEvaluatedStrategy } from './RetirementVillagePage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/** The annuity a candidate buys, in the shape the sentence and the warnings read. */
export function annuityOfStrategy(strategy: RvEvaluatedStrategy, factFind: DraftFactFind): RvInBriefAnnuity | null {
    const terms = (strategy as { annuityTerms?: { purchaseAmount?: unknown; residualPercent?: unknown; owner?: unknown } | null }).annuityTerms;
    const amount = Number(terms?.purchaseAmount) || 0;
    if (!(amount > 0)) return null;
    const owner = terms?.owner === 'partner' ? 'partner' : 'client';
    const ownerName = (owner === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you';
    return { amount, residualPercent: Number(terms?.residualPercent) || 0, ownerName };
}

/** The age-and-health warning for a candidate's annuity — the aged care rule, applied here. */
export function annuityHorizonWarningOf(strategy: RvEvaluatedStrategy, factFind: DraftFactFind): string | null {
    const terms = (strategy as { annuityTerms?: { purchaseAmount?: unknown; residualPercent?: unknown; owner?: unknown } | null }).annuityTerms;
    if (!(Number(terms?.purchaseAmount) > 0)) return null;
    const flag = annuityHorizonFlag({
        factFind: factFind as unknown as HorizonFactFind,
        residualCapital: (Number(terms?.residualPercent) || 0) / 100,
        owner: terms?.owner,
    });
    return flag?.warning ?? null;
}

/**
 * What "What could go wrong" says about this plan in particular: the annuity's age-and-health warning and the
 * stamp duty on an investment property it buys. The duty sits in the sale's details (sellHomeDetails) on a
 * tested plan — the top-level field the risk panel read is only ever set by older records — so both are read
 * (22 Oct 2026: the chosen plan's panel passed none of this, and the tested plan's never found the duty).
 */
export function rvRiskSpecificsOf(strategy: RvEvaluatedStrategy, factFind: DraftFactFind): { horizonWarning: string | null; stampDuty: number | null } {
    const inputs = (strategy.inputs || {}) as { investmentPropertyStampDuty?: unknown; sellHomeDetails?: { purchaseInvestmentProperty?: unknown; investmentPropertyStampDuty?: unknown } };
    const sale = inputs.sellHomeDetails;
    const duty = Number(inputs.investmentPropertyStampDuty) || (Number(sale?.purchaseInvestmentProperty) > 0 ? Number(sale?.investmentPropertyStampDuty) || 0 : 0);
    return { horizonWarning: annuityHorizonWarningOf(strategy, factFind), stampDuty: duty > 0 ? duty : null };
}

export interface RvStrategyCardProps {
    strategy: RvEvaluatedStrategy;
    factFind: DraftFactFind;
    badges: string[];
    /** Set when this is the best of the field for a goal WITHOUT beating staying put — said on the card. */
    belowBaseline?: string;
    /** The second card's one line: the trade-off that earns it a place (planChoiceRule.ts). */
    tradeOffNote?: string;
    /** The staying-put candidate — pension, cashflow and estate every tile is measured against. */
    baseline: RvEvaluatedStrategy | null | undefined;
    entryFee: number;
    legacyYears: number;
    clientName: string;
    partnerName: string;
    isSelected: boolean;
    onSelect: () => void;
    onExplain: () => void;
    /** Opens "What could go wrong" for this plan (StrategyRisksPanel). */
    onRisks: () => void;
    /** Generates the Statement of Advice for this plan — offered only once it is selected. */
    onGenerateSoa?: () => void;
    /** The same plan with the other kind of asset, and the gap — alternativeAssetNote.ts. */
    alternative?: AlternativeAssetNote | null;
    /** The annuity terms and quote control, rendered by the page — only for a strategy that buys one. */
    annuityBlock?: React.ReactNode;
    /** For the cashflow-covered leader: which case chose it (cashflowCoveredGoal.ts). */
    goalNote?: string | null;
    /** Only the "what this strategy does" block — the detail layer under the step 3 plan card (rebuild, 21 Oct 2026). */
    detailOnly?: boolean;
}

/** The line under a tile: staying put's figure and the change, coloured by whether the move is good. */
const StayingPut = ({ value, baseline, upIsGood }: { value: number; baseline: number | undefined; upIsGood: boolean }) => {
    if (baseline == null) return null;
    const d = value - baseline;
    const flat = Math.round(d) === 0;
    const good = upIsGood ? d > 0 : d < 0;
    return (
        <p className="mt-1 text-[13px] tabular-nums text-slate-500">
            staying put {formatCurrency(baseline)} ·{' '}
            <span className={`font-semibold ${flat ? 'text-slate-400' : good ? 'text-emerald-700' : 'text-rose-700'}`}>
                {flat ? 'no change' : `${d > 0 ? '+' : '−'}${formatCurrency(Math.abs(d))}`}
            </span>
        </p>
    );
};

const Tile = ({ label, value, tone, children }: { label: string; value: string; tone: 'good' | 'bad' | 'neutral'; children?: React.ReactNode }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className={`mt-1 text-[20px] font-bold leading-none tabular-nums ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>{value}</p>
        {children}
    </div>
);

const RvStrategyCard = ({
    strategy,
    factFind,
    badges,
    belowBaseline,
    tradeOffNote,
    baseline,
    entryFee,
    legacyYears,
    clientName,
    partnerName,
    isSelected,
    onSelect,
    onExplain,
    onRisks,
    onGenerateSoa,
    alternative = null,
    annuityBlock,
    goalNote = null,
    detailOnly = false,
}: RvStrategyCardProps) => {
    const snap = strategy.snapshot || {};
    const base = baseline?.snapshot || {};
    const inputs = (strategy.inputs || {}) as { fundFrom?: string; rentHome?: boolean; sellHomeDetails?: { salePrice?: number }; rentDetails?: { rentAmount?: number } };
    const fundFrom = String(inputs.fundFrom || 'liquid');
    const rentHome = !!inputs.rentHome || fundFrom === 'rent_home';
    const rows = strategy.projections || [];
    const planLegacy = snap.legacyValue ?? (rows.length ? estateOf(rows[Math.min(rows.length, Number(legacyYears) || rows.length) - 1]) : null);
    // The engine's own funding rows: on a property route the first is the sale, the second (if any)
    // is what savings paid — so the sentence and the "How it's funded" box say the same thing.
    const funding = strategy.fundingBreakdown || [];
    const isPropertyRoute = fundFrom === 'sell_home' || fundFrom === 'investment_property';
    const fromSale = isPropertyRoute ? Number(funding[0]?.amount) || 0 : null;
    const fromSavings = isPropertyRoute ? funding.filter((f) => /savings/i.test(f.label)).reduce((s, f) => s + (Number(f.amount) || 0), 0) : null;
    // Moving from another village: the current unit's payout is the first source (8 Oct 2026).
    const fromOldUnit = funding.filter((f) => /current village unit/i.test(f.label)).reduce((s, f) => s + (Number(f.amount) || 0), 0);
    const annuity = annuityOfStrategy(strategy, factFind);
    const nilResidual = annuity && Math.round(annuity.residualPercent) === 0 ? annuity : null;
    const horizonWarning = annuityHorizonWarningOf(strategy, factFind);

    const flow: MoneyFlowStep[] = [];
    if (fundFrom === 'sell_home') {
        flow.push({ amount: Number(inputs.sellHomeDetails?.salePrice) || 0, label: 'home sold', sub: 'proceeds fund the move', tone: 'sold' });
    } else if (fundFrom === 'investment_property') {
        flow.push({ amount: fromSale || entryFee, label: 'property sold', sub: 'net proceeds, after costs and CGT', tone: 'sold' });
    }
    flow.push({
        amount: entryFee,
        label: 'entry contribution',
        sub: fromOldUnit > 0 ? `${formatCurrency(fromOldUnit)} of it from the current unit's payout, the rest from savings · refundable less the departure fee` : fromSavings && fromSavings > 0 ? `${formatCurrency(fromSavings)} of it from savings · refundable less the departure fee` : (fundFrom === 'liquid' || fundFrom === 'rent_home' ? 'from savings · refundable less the departure fee' : 'refundable less the departure fee'),
        tone: 'good',
    });
    if (rentHome) flow.push({ amount: (Number(inputs.rentDetails?.rentAmount) || 0) * 52, label: 'home kept, rented out', sub: 'rent a year, assessable', tone: 'good' });
    if (nilResidual) flow.push({ amount: nilResidual.amount, label: 'lifetime annuity', sub: 'nothing returned on death', tone: 'warn' });

    const what = (
            <PlainWordsCard eyebrow="What this strategy does" className={detailOnly ? '' : 'mt-3'}>
                {goalNote && (
                    <p className="mb-2.5 rounded-lg border-l-4 border-blue-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-blue-900">{goalNote}</p>
                )}
                <MoneyFlow steps={flow.filter((s) => s.amount > 0)} />
                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-700">
                    <RvInBrief
                        entryFee={entryFee}
                        fundFrom={fundFrom}
                        rentHome={rentHome}
                        fromSaleAmount={fromSale}
                        fromSavingsAmount={fromSavings}
                        pensionBefore={Number(base.pension) || 0}
                        pensionAfter={Number(snap.pension) || 0}
                        cashBefore={Number(base.netCashflow) || 0}
                        cashAfter={Number(snap.netCashflow) || 0}
                        planLegacy={planLegacy}
                        baseLegacy={base.legacyValue ?? null}
                        legacyYears={legacyYears}
                        annuity={annuity}
                    />
                </p>
                {(nilResidual || horizonWarning) && (
                    <div className="mt-2.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                        <span className="font-semibold">Worth knowing:</span>{' '}
                        {nilResidual && (
                            <>The estate ends behind staying put mainly because {nilResidualAnnuityText(nilResidual)}. The income is guaranteed for life; the capital is not coming back.{' '}</>
                        )}
                        {horizonWarning}
                    </div>
                )}
                {alternative && (
                    <p className="mt-2.5 rounded-lg border-l-4 border-blue-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-blue-900">
                        <span className="font-semibold">{alternative.question}</span> {alternative.answer}
                    </p>
                )}
                {funding.length > 0 && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">How it&apos;s funded</p>
                        <ul className="text-sm text-slate-700 space-y-0.5">
                            {funding.map((f, i) => (
                                <li key={i} className="flex justify-between"><span>{f.label}</span><span className="font-medium tabular-nums">{formatCurrency(f.amount)}</span></li>
                            ))}
                        </ul>
                    </div>
                )}
                <RvStrategyExplainer
                    strategy={strategy}
                    baseline={baseline ?? null}
                    entryFee={entryFee}
                    legacyYears={legacyYears}
                    clientName={clientName}
                    partnerName={partnerName}
                    clientUnderPensionAge={!!factFind?.client?.dob && (calculateAge(String(factFind?.client?.dob)) ?? 999) < PENSION_AGE}
                    partnerUnderPensionAge={factFind?.adviceFor === 'couple' && !!factFind?.partner?.dob && (calculateAge(String(factFind?.partner?.dob)) ?? 999) < PENSION_AGE}
                />
                {annuityBlock}
            </PlainWordsCard>
    );
    if (detailOnly) return what;

    return (
        <div className={`rounded-xl border border-l-4 p-4 shadow-sm transition-all ${isSelected ? 'border-blue-500 border-l-blue-600 bg-blue-50/40 ring-1 ring-blue-200' : belowBaseline ? 'border-slate-200 border-l-slate-400 bg-white' : 'border-slate-200 border-l-blue-500 bg-white'}`}>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                {badges.map((b) => (
                    <Badge key={b} tone={belowBaseline ? 'neutral' : 'info'}>{b}</Badge>
                ))}
            </div>
            <h4 className="text-lg font-semibold text-gray-900">{strategy.name}</h4>
            <p className="text-sm text-gray-600 mt-1">{strategy.description}</p>
            {belowBaseline && (
                <p className="mt-1.5 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700">
                    {belowBaseline}
                </p>
            )}
            {tradeOffNote && (
                <p className="mt-1.5 rounded-lg border border-slate-200/70 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-700" data-testid="trade-off-note">{tradeOffNote}</p>
            )}

            {what}

            <div className="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-3">
                <Tile label="Age Pension" value={formatCurrency(Number(snap.pension) || 0)} tone="neutral">
                    <StayingPut value={Number(snap.pension) || 0} baseline={base.pension} upIsGood />
                </Tile>
                <Tile label="Cashflow (year 1)" value={formatCurrency(Number(snap.netCashflow) || 0)} tone={(Number(snap.netCashflow) || 0) < 0 ? 'bad' : 'good'}>
                    <StayingPut value={Number(snap.netCashflow) || 0} baseline={base.netCashflow} upIsGood />
                </Tile>
                <Tile label={`Estate (year ${legacyYears})`} value={planLegacy != null ? formatCurrency(planLegacy) : '—'} tone="neutral">
                    {planLegacy != null && <StayingPut value={planLegacy} baseline={base.legacyValue} upIsGood />}
                </Tile>
            </div>

            {/* Three buttons, not five (owner, 2 Oct 2026): understand it, see what could go wrong,
                choose it. The year-by-year deep dive lives inside "Explain this". */}
            <div className="mt-4 flex gap-3">
                <Button onClick={onExplain} variant="secondary" size="sm" icon={BookOpen} className="flex-1">Explain this</Button>
                <Button onClick={onRisks} variant="secondary" size="sm" icon={ShieldAlert} className="flex-1">What could go wrong</Button>
                <Button onClick={onSelect} variant="primary" size="sm" icon={CheckCircle} className="flex-1">{isSelected ? 'Selected ✓' : 'Select Strategy'}</Button>
            </div>
            {isSelected && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2 text-sm text-emerald-900 shadow-sm">
                    <p className="min-w-0 flex-1">
                        <span className="font-semibold">This is your selected strategy.</span> It is saved with your file, and the advice document is written from it.
                    </p>
                    {onGenerateSoa && (
                        <button
                            type="button"
                            onClick={onGenerateSoa}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
                        >
                            Generate your Statement of Advice →
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default RvStrategyCard;
